/**
 * useGroupInvitationNotifications Hook
 * Monitors group invitations and sends notifications
 */

import { useEffect, useRef } from 'react';
import { useSettingsStore } from '@/stores/settings-store';
import { useAuthStore } from '@/stores/auth-store';
import { useNotifications } from './useNotifications';
import { apiGet } from '@/services/api-client';
import { hasStoredExpoPushToken } from '@/utils/expo-push-token';

interface Invite {
  id: string;
  groupId: string;
  groupName: string;
  invitedByName: string;
  status: 'pending' | 'accepted' | 'rejected';
  createdAt: string;
}

export function useGroupInvitationNotifications() {
  const { sendNotification, permissionStatus } = useNotifications();
  const { notifications: notificationSettings } = useSettingsStore();
  const { accessToken, user, isAuthenticated } = useAuthStore();
  const seenInviteIdsRef = useRef<Set<string>>(new Set());
  const isInitializedRef = useRef(false);

  useEffect(() => {
    console.log('[useGroupInvitationNotifications] Effect running. Auth:', isAuthenticated, 'Settings:', notificationSettings.groupInvitations, 'Permission:', permissionStatus.granted, 'User ID:', user?.id);
    
    if (!isAuthenticated || !notificationSettings.groupInvitations || !permissionStatus.granted || !accessToken || !user?.id) {
      console.log('[useGroupInvitationNotifications] Not starting - missing requirements');
      return;
    }
    // Skip if user has push token (mobile app) - they get backend push notifications instead
    const hasPushToken = hasStoredExpoPushToken();
    if (hasPushToken) {
      console.log('[useGroupInvitationNotifications] Skipping - user has push token, will get backend notifications');
      return;
    }
    console.log('[useGroupInvitationNotifications] Starting notification polling...');

    // Initial load - mark all existing invitations as seen
    (async () => {
      try {
        console.log('[useGroupInvitationNotifications] Fetching initial invitations...');
        const res = await apiGet<Invite[]>('/invites');
        console.log('[useGroupInvitationNotifications] Initial fetch response:', res.success, 'Data:', res.data);
        
        if (res.success && res.data) {
          const pendingInvites = res.data.filter(invite => invite.status === 'pending');
          pendingInvites.forEach(invite => seenInviteIdsRef.current.add(invite.id));
          isInitializedRef.current = true;
          console.log('[useGroupInvitationNotifications] Initial load - marked', pendingInvites.length, 'existing invitations as seen');
        }
      } catch (error) {
        console.error('[useGroupInvitationNotifications] Failed to initialize:', error);
      }
    })();

    // Poll for group invitations every 30 seconds
    const intervalId = setInterval(async () => {
      // Check for push token before each poll - stop polling if token exists
      const hasPushToken = hasStoredExpoPushToken();
      if (hasPushToken) {
        console.log('[useGroupInvitationNotifications] Push token detected, stopping polling');
        clearInterval(intervalId);
        return;
      }

      try {
        console.log('[useGroupInvitationNotifications] Polling for invitations... Initialized:', isInitializedRef.current);
        const res = await apiGet<Invite[]>('/invites');
        
        if (!res.success || !res.data) {
          console.log('[useGroupInvitationNotifications] Poll failed or no data');
          return;
        }
        
        // Filter for pending invitations
        const pendingInvites = res.data.filter(invite => invite.status === 'pending');
        console.log('[useGroupInvitationNotifications] Found', pendingInvites.length, 'pending invitations');
        
        // On first run, just mark all invitations as seen (don't notify for existing invitations)
        if (!isInitializedRef.current) {
          pendingInvites.forEach(invite => seenInviteIdsRef.current.add(invite.id));
          isInitializedRef.current = true;
          console.log('[useGroupInvitationNotifications] First poll - marked', pendingInvites.length, 'invitations as seen');
          return;
        }
        
        // Check for new invitations
        for (const invite of pendingInvites) {
          const isSeen = seenInviteIdsRef.current.has(invite.id);
          console.log('[useGroupInvitationNotifications] Checking invite', invite.id, 'from', invite.invitedByName, '- Seen:', isSeen);
          
          if (!isSeen) {
            seenInviteIdsRef.current.add(invite.id);
            
            console.log('[useGroupInvitationNotifications] 🔔 NEW INVITATION! Sending notification for:', invite.groupName);
            
            // Send notification for new invitation
            await sendNotification({
              title: 'Ny gruppinbjudan',
              body: `${invite.invitedByName} bjuder in dig till ${invite.groupName}`,
              data: {
                type: 'group-invitation',
                inviteId: invite.id,
                groupId: invite.groupId,
                url: '/app/groups/notifications',
              },
            });
          }
        }
        
        // Clean up old invite IDs from memory (keep last 100)
        if (seenInviteIdsRef.current.size > 100) {
          const inviteIds = Array.from(seenInviteIdsRef.current);
          seenInviteIdsRef.current = new Set(inviteIds.slice(-100));
        }
      } catch (error) {
        console.error('[useGroupInvitationNotifications] Failed to check group invitations:', error);
      }
    }, 30 * 1000); // 30 seconds

    return () => {
      console.log('[useGroupInvitationNotifications] Cleaning up...');
      clearInterval(intervalId);
      isInitializedRef.current = false;
    };
  }, [isAuthenticated, notificationSettings.groupInvitations, permissionStatus.granted, accessToken, user?.id, sendNotification]);
}
